import { createFileRoute } from '@tanstack/react-router'
import { pageSeo } from '@/lib/seo'
import { msg } from '@lingui/core/macro'
import { useMutation } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect } from 'react'
import { usePostLoginRedirect } from '@/hooks/use-post-login-redirect'
import { endLogout } from '@/lib/logout-signal'
import { useLingui } from '@lingui/react/macro'
import { useAppForm } from '@/lib/use-app-form'
import { useAppStore } from '@/lib/app-store'
import { clearQueryClient, queryClient, trpc } from '@/main'
import { SocialButtons } from '@/routes/_app/-components/social-buttons'
import { AuthCard } from '@/routes/_app/-components/shared/auth-card'
import { AuthCardSkeleton } from '@/routes/_app/-components/app-layout-skeleton'
import { Separator } from '@/components/ui/separator'
import { resolveRedirectTarget } from '@/lib/redirect-target'
import { saveCredential } from '@/lib/save-credentials'
import * as v from 'valibot'

export const Route = createFileRoute('/_public/login')({
  validateSearch: v.object({
    // Sanitize on the way in: unwrap any nested `/login?redirect=…` so a bad inbound URL can't be read
    // back out (or forwarded to the /register link) as a self-referential redirect. See resolveRedirectTarget.
    redirect: v.pipe(
      v.optional(v.string()),
      v.transform((value) => resolveRedirectTarget(value)),
    ),
  }),
  head: (ctx) =>
    pageSeo(ctx, {
      path: '/login',
      title: msg`Log in`,
      description: msg`Log in to D’Anchiano to assess, compare and manage the talent of your company with psychographic data.`,
    }),
  component: RouteComponent,
  pendingComponent: () => <AuthCardSkeleton logo social forgot />,
})

function RouteComponent() {
  const { redirect } = Route.useSearch()
  const { t } = useLingui()
  const redirectAfterLogin = usePostLoginRedirect()
  const { mutateAsync: login } = useMutation(trpc.auth.login.mutationOptions())

  // Logout is now a client navigation (not a full reload), so clear the "logging out" signal once we
  // land here — otherwise it would persist into the next session and leave the topbar stuck neutral.
  // Safe on this bare /login page: the topbar isn't mounted, so resetting causes no flash.
  useEffect(() => endLogout(), [])

  // A signed-in, fully-registered visitor never reaches this form: the `_app` guard sends them on (entry-access).
  // Social login resolves its destination server-side (shared/post-login), so it never bounces back through
  // /login; the password flow redirects via usePostLoginRedirect on submit.

  const form = useAppForm({
    // Validate on submit only (not onChange). On mobile, autofill/keyboard interactions can flip a
    // field to "touched" the moment it's focused; with onChange validation that surfaced an
    // "invalid email" error before the user had typed anything. onSubmit keeps the field clean until
    // the user actually tries to log in — the standard login-form UX.
    validators: {
      onSubmit: v.object({
        email: v.pipe(v.string(), v.email()),
        password: v.pipe(v.string(), v.minLength(1)),
      }),
    },
    defaultValues: {
      email: '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      const result = await login(value)
      // Drop ALL pre-login data (a client nav keeps every query answered for the anonymous viewer) and re-seed the
      // session below, with NOTHING awaited first: the revalidation's session refetch would land before the clear,
      // which then tore down the painted destination (docs/notes/app/query-persistence.md, "password login").
      clearQueryClient()
      // The welcome-guide "Close" latch is in-memory and only resets on a full page reload, and login is a
      // client navigation — unre-armed, a session that had closed the guide would carry the latch into the
      // next login and "Close" would behave like "Don't show again". Re-armed, "Close" reopens the guide on
      // the next entry (while tour === 0) and "Don't show again" (tour = 1) still suppresses it.
      useAppStore.getState().resetWelcome()
      // Re-seed the user cache with the just-authenticated user so the client navigation below reads
      // the logged-in state immediately (a client nav otherwise reads the now-empty `getUser` and
      // bounces back to /login).
      queryClient.setQueryData(trpc.auth.getUser.queryKey(), result)
      redirectAfterLogin(result)
      // A client navigation keeps this document, so the browser can still offer to save the password.
      void saveCredential(value.email, value.password)
    },
  })

  return (
    <AuthCard
      home
      title={t`Welcome back`}
      subtitle={t`Sign in to your D'Anchiano account`}
      contentClassName="flex flex-col gap-5"
      footer={
        <>
          {t`Don't have an account?`}{' '}
          <Link to="/register" search={{ redirect }} className="text-primary font-medium hover:underline">
            {t`Sign up`}
          </Link>
        </>
      }
    >
      <SocialButtons login />

      <div className="flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-muted-foreground text-xs">{t`or`}</span>
        <Separator className="flex-1" />
      </div>

      <form
        // noValidate: type="email" would otherwise trigger the browser's native (English,
        // unstyled) validation bubble on submit. We let the zod onSubmit validator own it so the
        // error is the app's styled, localized field message instead.
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          form.handleSubmit()
        }}
        className="flex flex-col gap-3"
      >
        <form.AppField
          name="email"
          children={(field) => (
            <field.TextField label={t`Email`} type="email" inputMode="email" autoComplete="username" />
          )}
        />

        <form.AppField
          name="password"
          children={(field) => <field.PasswordField label={t`Password`} autoComplete="current-password" />}
        />

        <Link to="/begin-password-reset" className="text-muted-foreground text-sm hover:underline">
          {t`Forgot your password?`}
        </Link>

        <form.AppForm>
          <form.SubmitButton label={t`Login`} />
        </form.AppForm>
      </form>
    </AuthCard>
  )
}
